import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Mail } from 'lucide-react';
import LazyImage from './LazyImage';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Warm Premium Palette
  const colors = {
    primaryTeal: '#3D6B67',
    primaryBrown: '#5C4033',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_impact-consulting-1/artifacts/0x7ttskj_image.png';

  const links = {
    platform: [
      { label: 'CSR Marketplace', path: '/' },
      { label: 'Compliance Center', path: '/governance' },
      { label: 'Impact Analytics', path: '/saas/admin/reports' },
      { label: 'Partner Network', path: '/partners/sponsors' }
    ],
    resources: [
      { label: 'CSR Landscape', path: '/csr-landscape' },
      { label: 'Documentation', path: '/docs' },
      { label: 'API Reference', path: '/api-docs' },
      { label: 'Help Center', path: '/help' }
    ],
    company: [
      { label: 'About Us', path: '/about-us' },
      { label: 'Contact', path: '/contact' },
      { label: 'Privacy', path: '/privacy' },
      { label: 'Terms', path: '/terms' }
    ]
  };

  return (
    <footer data-testid="main-footer" style={{ backgroundColor: colors.primaryTeal }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="py-16 md:py-20 grid grid-cols-2 md:grid-cols-5 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="overflow-hidden rounded-lg" style={{ height: '44px', width: '44px' }}>
                <LazyImage 
                  src={logoUrl} 
                  alt="CSR Exchange Logo" 
                  className="h-full w-full"
                  placeholderColor="rgba(255, 255, 255, 0.1)"
                  style={{ height: '68px', width: 'auto', marginTop: '-2px', filter: 'brightness(1.1)', objectFit: 'cover', objectPosition: 'top' }}
                  data-testid="footer-logo"
                />
              </div>
              <span className="text-2xl font-bold tracking-tight" style={{ color: colors.textOnDark }}>
                CSR Exchange
              </span>
            </div>
            <p className="text-sm leading-relaxed mb-8 max-w-xs" style={{ color: colors.textOnDarkMuted }}>
              Enterprise CSR infrastructure for strategy, compliance, and measurable impact across India.
            </p>
            <div className="flex items-center gap-3">
              {[
                { Icon: Linkedin, href: 'https://linkedin.com' },
                { Icon: Twitter, href: 'https://twitter.com' },
                { Icon: Mail, href: 'mailto:contact@csrexchange.in' }
              ].map(({ Icon, href }, idx) => (
                <a 
                  key={idx}
                  href={href}
                  target={idx !== 2 ? '_blank' : undefined}
                  rel={idx !== 2 ? 'noopener noreferrer' : undefined}
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: colors.textOnDark }} />
                </a>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(links).map(([title, items]) => (
            <div key={title}>
              <h4 className="text-sm font-semibold mb-5 capitalize tracking-wide" style={{ color: colors.textOnDark }}>
                {title}
              </h4>
              <ul className="space-y-3">
                {items.map((link, idx) => (
                  <li key={idx}>
                    <Link 
                      to={link.path} 
                      className="text-sm transition-colors duration-200"
                      style={{ color: colors.textOnDarkMuted }}
                      onMouseEnter={(e) => e.currentTarget.style.color = colors.textOnDark}
                      onMouseLeave={(e) => e.currentTarget.style.color = colors.textOnDarkMuted}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div 
          className="py-6 flex flex-col md:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid rgba(255, 255, 255, 0.12)' }}
        >
          <p className="text-sm" style={{ color: colors.textOnDarkMuted }}>
            © {currentYear} CSR Exchange. All rights reserved.
          </p>
          <div className="flex items-center gap-8 text-sm">
            {['Privacy', 'Terms', 'Support'].map((item, idx) => (
              <Link 
                key={idx}
                to={`/${item.toLowerCase() === 'support' ? 'contact' : item.toLowerCase()}`}
                className="transition-colors duration-200"
                style={{ color: colors.textOnDarkMuted }}
                onMouseEnter={(e) => e.currentTarget.style.color = colors.textOnDark}
                onMouseLeave={(e) => e.currentTarget.style.color = colors.textOnDarkMuted}
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
